/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import { Character } from "@ictlearningsciences/pal3-core/character";
import {
  AudioMode,
  resetSettings,
  updateSettings,
} from "../store/slices/settings";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearCache,
  loadDialogue,
  loadGoals,
  loadLearnerTypes,
  loadStudies,
  loadSurveys,
} from "../store/slices/data";
import { resetRecords } from "../store/slices/userData";
import { LessonLearningMode } from "@ictlearningsciences/pal3-core/types";

export const VOICE_PRIORITIES = [
  "Google US English",
  "Microsoft Ava Online (Natural) - English (United States)",
  "Microsoft Andrew Online (Natural) - English (United States)",
  "Arthur",
  "Daniel",
  "Samantha",
];

export interface Voice {
  type: "tts" | "audio";
  id: string;
  name: string;
  recommended?: boolean;
}

export interface UseWithSettings {
  setLearningMode: (learningMode: LessonLearningMode) => void;
  setCharacter: (character: Character) => void;
  setVolume: (volume: number) => void;
  setAudioMode: (mode: AudioMode) => void;
  resetSettings: () => void;
  resetRecords: (date?: Date) => void;
  clearCache: () => void;
  getVoices: (mine?: boolean) => Voice[];
  setVoice: (voice: Voice) => void;
  togglePassiveLesson: () => void;
}

export function useWithSettings(): UseWithSettings {
  const dispatch = useAppDispatch();
  const { audioMode, voiceTTS, voiceRecorded, showPassiveScore } =
    useAppSelector((state) => state.settings);

  function setLearningMode(learningMode: LessonLearningMode) {
    dispatch(updateSettings({ learningMode }));
  }

  function setCharacter(character: Character): void {
    dispatch(updateSettings({ character }));
  }

  function setVolume(volume: number): void {
    dispatch(updateSettings({ volume: volume }));
  }

  function setAudioMode(mode: AudioMode): void {
    dispatch(updateSettings({ audioMode: mode }));
  }

  function togglePassiveLesson(): void {
    dispatch(updateSettings({ showPassiveScore: !showPassiveScore }));
  }

  function _resetSettings(): void {
    dispatch(resetSettings());
  }

  function _clearCache(): void {
    dispatch(clearCache());
    dispatch(loadLearnerTypes());
    dispatch(loadGoals());
    dispatch(loadDialogue());
    dispatch(loadSurveys());
    dispatch(loadStudies());
  }

  function _resetRecords(date?: Date): void {
    dispatch(resetRecords(date));
  }

  function setVoice(voice: Voice): void {
    if (voice.type === "tts") {
      dispatch(updateSettings({ voiceTTS: voice.id }));
    } else {
      dispatch(updateSettings({ voiceRecorded: voice.id }));
    }
  }

  function getVoices(mine?: boolean): Voice[] {
    const voices: Voice[] = [];
    if (audioMode === AudioMode.VOICE_ONLY || audioMode === AudioMode.BOTH) {
      voices.push({
        type: "audio",
        id: "clint_anderson",
        name: "Battle Buddy (Voice Recorded)",
        recommended: true,
      });
    }
    if (audioMode === AudioMode.TTS_ONLY || audioMode === AudioMode.BOTH) {
      const sp: Voice[] = speechSynthesis
        .getVoices()
        .filter((a) => a.lang === "en-US" || a.lang === "en-GB")
        .sort((a, b) => {
          const idxA = VOICE_PRIORITIES.findIndex((v) => v === a.name);
          const idxB = VOICE_PRIORITIES.findIndex((v) => v === b.name);
          if (idxA === -1 && idxB !== -1) {
            return 1;
          } else if (idxA !== -1 && idxB === -1) {
            return -1;
          } else if (idxA !== -1 && idxB !== -1) {
            return idxA - idxB;
          } else if (a.lang === "en-US" && b.lang !== "en-US") {
            return -1;
          } else if (b.lang === "en-US") {
            return 1;
          } else if (a.lang === b.lang) {
            return a.name.localeCompare(b.name);
          } else {
            return a.lang.localeCompare(b.lang);
          }
        })
        .map((s) => ({
          type: "tts",
          id: s.name,
          name: `${s.name} (${s.lang})`,
          recommended: VOICE_PRIORITIES.includes(s.name),
        }));
      voices.push(...sp);
    }
    voices.sort((a, b) => {
      if (a.id === voiceRecorded) return -1;
      if (b.id === voiceRecorded) return 1;
      if (a.id === voiceTTS) return -1;
      if (b.id === voiceTTS) return 1;
      return 0;
    });
    if (mine) {
      return voices.filter((v) => v.id === voiceTTS || v.id === voiceRecorded);
    }
    return voices;
  }

  return {
    setLearningMode,
    setCharacter,
    setVolume,
    setAudioMode,
    togglePassiveLesson,
    setVoice,
    getVoices,
    resetSettings: _resetSettings,
    resetRecords: _resetRecords,
    clearCache: _clearCache,
  };
}
